import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { loggerId, type LoggerId } from './Logger.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.device_api_key_ */
export type DeviceApiKeyId = string & { __brand: 'DeviceApiKeyId' }

/** Represents the table public.device_api_key_ */
export default interface DeviceApiKeyTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<DeviceApiKeyId, DeviceApiKeyId | undefined, DeviceApiKeyId>

  /** Database type: pg_catalog.int4 */
  loggerId: ColumnType<LoggerId, LoggerId, LoggerId>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId, UserId, UserId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.timestamptz */
  expiresAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.timestamptz */
  revokedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.timestamptz */
  lastUsedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.inet */
  lastUsedIp: ColumnType<string | null, string | null, string | null>
}

export type DeviceApiKey = Selectable<DeviceApiKeyTable>

export type DeviceApiKeyInitializer = Insertable<DeviceApiKeyTable>

export type DeviceApiKeyMutator = Updateable<DeviceApiKeyTable>

export const deviceApiKeyId = z.string() as unknown as z.Schema<DeviceApiKeyId>

export const deviceApiKey = z.object({
  id: deviceApiKeyId,
  loggerId: loggerId,
  createdBy: userId,
  createdAt: z.coerce.date(),
  expiresAt: z.coerce.date(),
  revokedAt: z.coerce.date().nullable().optional(),
  lastUsedAt: z.coerce.date().nullable().optional(),
  lastUsedIp: z.string().nullable().optional(),
}) as unknown as z.Schema<DeviceApiKey>

export const deviceApiKeyInitializer = z.object({
  id: deviceApiKeyId.optional(),
  loggerId: loggerId,
  createdBy: userId,
  createdAt: z.coerce.date().optional(),
  expiresAt: z.coerce.date().optional(),
  revokedAt: z.coerce.date().optional().nullable(),
  lastUsedAt: z.coerce.date().optional().nullable(),
  lastUsedIp: z.string().optional().nullable(),
}) as unknown as z.Schema<DeviceApiKeyInitializer>

export const deviceApiKeyMutator = z.object({
  id: deviceApiKeyId.optional(),
  loggerId: loggerId.optional(),
  createdBy: userId.optional(),
  createdAt: z.coerce.date().optional(),
  expiresAt: z.coerce.date().optional(),
  revokedAt: z.coerce.date().optional().nullable(),
  lastUsedAt: z.coerce.date().optional().nullable(),
  lastUsedIp: z.string().optional().nullable(),
}) as unknown as z.Schema<DeviceApiKeyMutator>
